import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { message, Modal } from 'antd';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Building2, Loader2, Ban, CheckCircle, Globe, Mail, Calendar, Users, HardDrive, Activity, Pencil, X, LogIn } from 'lucide-react';
import { useAppSelector } from '../../app/hooks';
import { selectHasPermission } from '../../features/auth/authSlice';
import { getTenantById, deactivateTenant, activateTenant, terminateTenant, updateTenantEntitlements, impersonateTenantAdmin, type AdminTenant } from '../../services/tenantService';
import { useConfirmWithReason } from '../../components/console';
import TenantEntitlements from './TenantEntitlements';
import { TenantCommercial } from './TenantCommercial';
import TenantAuditAccess from './TenantAuditAccess';

const MAIN_APP_URL = (import.meta.env.VITE_MAIN_APP_URL as string) || 'https://app.kazipeople.com';

const PLANS = ['free', 'standard', 'premium', 'enterprise'];

const cap = (s?: string | null) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '—');

const Row: React.FC<{ icon: React.ReactNode; label: string; value: React.ReactNode }> = ({ icon, label, value }) => (
  <div className="flex items-center gap-3 py-2">
    <span className="text-muted-foreground">{icon}</span>
    <span className="text-sm text-muted-foreground w-40">{label}</span>
    <span className="text-sm text-foreground font-medium">{value}</span>
  </div>
);

const TenantDetailsPage: React.FC = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const canSuspend = useAppSelector(selectHasPermission('tenant:suspend'));
  const canEditPlan = useAppSelector(selectHasPermission('tenant:update:all'));
  // Entitlement grant/transition is enforced backend-side by product:entitlement:manage
  // (entitlements.controller.ts). Gate the manage controls on that exact permission so
  // a platform_admin without it sees read-only entitlements instead of a 403 dead-end.
  const canManageEntitlements = useAppSelector(selectHasPermission('product:entitlement:manage'));
  const canImpersonate = useAppSelector(selectHasPermission('user:impersonate'));
  // Audit-access panel: view requires cross-tenant read; grant/revoke requires
  // cross-tenant update (mirrors the auth-service platform endpoint gates).
  const canViewAuditAccess = useAppSelector(selectHasPermission('tenant:read:all'));
  const canManageAuditAccess = useAppSelector(selectHasPermission('tenant:update:all'));
  const { confirm, dialog } = useConfirmWithReason();
  const [impersonating, setImpersonating] = useState(false);

  const [tenant, setTenant] = useState<AdminTenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Plan & limits editor
  const [editOpen, setEditOpen] = useState(false);
  const [savingPlan, setSavingPlan] = useState(false);
  const [planVal, setPlanVal] = useState('');
  const [maxUsersVal, setMaxUsersVal] = useState('');

  const openEdit = () => {
    if (!tenant) return;
    setPlanVal((tenant.subscriptionPlan as string) || 'free');
    setMaxUsersVal(String((tenant.maxUsers as number | undefined) ?? ''));
    setEditOpen(true);
  };
  const savePlan = async () => {
    if (!tenant) return;
    setSavingPlan(true);
    try {
      const maxUsers = Number(maxUsersVal);
      await updateTenantEntitlements(tenant.id, {
        subscriptionPlan: planVal,
        ...(Number.isFinite(maxUsers) && maxUsers > 0 ? { maxUsers } : {}),
      });
      setTenant({ ...tenant, subscriptionPlan: planVal, maxUsers: Number.isFinite(maxUsers) ? maxUsers : tenant.maxUsers });
      message.success('Plan & limits updated');
      setEditOpen(false);
    } catch {
      message.error('Could not update plan');
    } finally {
      setSavingPlan(false);
    }
  };

  useEffect(() => {
    if (!tenantId) return;
    (async () => {
      setLoading(true);
      try {
        setTenant(await getTenantById(tenantId));
      } catch {
        message.error('Could not load tenant');
      } finally {
        setLoading(false);
      }
    })();
  }, [tenantId]);

  const toggleSuspend = async () => {
    if (!tenant) return;
    const active = tenant.isActive ?? true;
    const reason = await confirm({
      title: active ? `Suspend "${tenant.name}"?` : `Reactivate "${tenant.name}"?`,
      message: active
        ? 'Users lose access until the tenant is reactivated. This is recorded in the audit log.'
        : 'Restores access for this tenant. This is recorded in the audit log.',
      okText: active ? 'Suspend' : 'Reactivate',
      danger: active,
      reasonRequired: active,
    });
    if (reason === null) return;
    setBusy(true);
    try {
      if (active) await deactivateTenant(tenant.id, reason);
      else await activateTenant(tenant.id, reason);
      setTenant({ ...tenant, isActive: !active });
      message.success(active ? 'Tenant suspended' : 'Tenant reactivated');
    } catch {
      message.error('Could not update tenant');
    } finally {
      setBusy(false);
    }
  };

  const terminate = async () => {
    if (!tenant) return;
    const reason = await confirm({
      title: `Terminate "${tenant.name}"?`,
      message:
        'This soft-deletes the tenant — all access is blocked immediately. Reversible by support (clears the deletion), but treat it as destructive.',
      okText: 'Terminate tenant',
      danger: true,
      reasonRequired: true,
      confirmPhrase: tenant.name,
    });
    if (reason === null) return;
    setBusy(true);
    try {
      await terminateTenant(tenant.id, reason);
      setTenant({ ...tenant, isActive: false });
      message.success('Tenant terminated');
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not terminate tenant');
    } finally {
      setBusy(false);
    }
  };

  // Sign in AS this tenant's admin (audited). Mints a session server-side and
  // hands the tokens to the main app via the URL hash (never sent to a server).
  const startImpersonation = () => {
    if (!tenant) return;
    Modal.confirm({
      title: `Sign in as the admin of "${tenant.name}"?`,
      content:
        'You will be acting AS that user in their workspace, and this is recorded in the audit log. Use only for support.',
      okText: 'Sign in as admin',
      onOk: async () => {
        setImpersonating(true);
        try {
          const s = await impersonateTenantAdmin(tenant.id);
          // H-SEC-5: short-lived, non-renewable impersonation — no refresh token.
          const url = `${MAIN_APP_URL}/impersonate#at=${encodeURIComponent(s.accessToken)}`;
          window.open(url, '_blank', 'noopener');
        } catch (e: any) {
          message.error(e?.response?.data?.message || 'Could not start impersonation');
        } finally {
          setImpersonating(false);
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="py-16 flex items-center justify-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }
  if (!tenant) {
    return <div className="py-16 text-center text-muted-foreground">Tenant not found.</div>;
  }

  const active = tenant.isActive ?? true;
  const users = tenant._count?.users ?? (tenant.userCount as number | undefined) ?? 0;
  const maxUsers = tenant.maxUsers as number | undefined;

  return (
    <div className="space-y-6">
      {dialog}
      <button type="button" onClick={() => navigate('/tenants')} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="w-4 h-4" /> Back to tenants
      </button>

      <div className="flex items-center gap-4 flex-wrap">
        <span className="inline-flex items-center justify-center w-12 h-12 rounded-[12px] bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700 dark:text-orange-300">
          <Building2 className="w-6 h-6" />
        </span>
        <div className="flex-1 min-w-[200px]">
          <h1 className="text-h2 text-foreground flex items-center gap-2">
            {tenant.name}
            <Badge className={active ? 'bg-green-100 text-green-700' : 'bg-red-100 dark:bg-red-500/15 text-red-700'}>
              {active ? 'Active' : 'Suspended'}
            </Badge>
          </h1>
          <p className="text-muted-foreground">/{tenant.slug}</p>
        </div>
        {canSuspend && (
          <Button
            onClick={toggleSuspend}
            disabled={busy}
            className={active ? 'bg-red-500 hover:bg-red-600 text-white dark:bg-red-600 dark:hover:bg-red-500' : 'bg-green-600 hover:bg-green-700 text-white'}
          >
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : active ? <Ban className="w-4 h-4 mr-1" /> : <CheckCircle className="w-4 h-4 mr-1" />}
            {active ? 'Suspend tenant' : 'Reactivate tenant'}
          </Button>
        )}
        {canSuspend && (
          <Button
            variant="outline"
            onClick={terminate}
            disabled={busy}
            className="border-red-300 dark:border-red-500/40 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10"
          >
            Terminate
          </Button>
        )}
        {canEditPlan && !editOpen && (
          <Button variant="outline" onClick={openEdit}>
            <Pencil className="w-4 h-4 mr-1" /> Edit plan &amp; limits
          </Button>
        )}
        {canImpersonate && active && (
          <Button variant="outline" onClick={startImpersonation} disabled={impersonating}
            title="Sign in as this tenant's admin (audited)">
            {impersonating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <LogIn className="w-4 h-4 mr-1" />}
            Sign in as admin
          </Button>
        )}
      </div>

      {editOpen && (
        <Card className="border-orange-200">
          <CardHeader className="flex flex-row items-center">
            <CardTitle className="flex-1">Plan &amp; limits</CardTitle>
            <button type="button" onClick={() => setEditOpen(false)} aria-label="Close" className="text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-foreground" htmlFor="plan">Subscription plan</label>
              <select id="plan" value={planVal} onChange={(e) => setPlanVal(e.target.value)}
                className="mt-1 w-full rounded-[10px] border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-orange-400 dark:focus:border-orange-500">
                {PLANS.map((p) => <option key={p} value={p}>{cap(p)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground" htmlFor="maxusers">Max users (seats)</label>
              <input id="maxusers" type="number" min={1} value={maxUsersVal} onChange={(e) => setMaxUsersVal(e.target.value)}
                className="mt-1 w-full rounded-[10px] border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-orange-400 dark:focus:border-orange-500" />
            </div>
            <div className="sm:col-span-2 flex items-center gap-2">
              <Button onClick={savePlan} disabled={savingPlan} className="bg-primary hover:bg-orange-600 text-primary-foreground">
                {savingPlan && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Save
              </Button>
              <Button variant="outline" onClick={() => setEditOpen(false)} disabled={savingPlan}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle>Overview</CardTitle></CardHeader>
          <CardContent className="divide-y divide-border">
            <Row icon={<Globe className="w-4 h-4" />} label="Domain" value={(tenant.domain as string) || '—'} />
            <Row icon={<Activity className="w-4 h-4" />} label="Plan" value={cap(tenant.subscriptionPlan)} />
            <Row icon={<Activity className="w-4 h-4" />} label="Subscription" value={tenant.subscriptionStatus ? cap(tenant.subscriptionStatus) : 'No billing subscription yet'} />
            <Row icon={<Mail className="w-4 h-4" />} label="Admin email" value={(tenant.adminEmail as string) || '—'} />
            <Row icon={<Calendar className="w-4 h-4" />} label="Created" value={fmtDate(tenant.createdAt)} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Usage &amp; limits</CardTitle></CardHeader>
          <CardContent className="divide-y divide-border">
            <Row icon={<Users className="w-4 h-4" />} label="Users" value={maxUsers ? `${users} / ${maxUsers}` : `${users}`} />
            {maxUsers && users >= maxUsers ? <p className="text-xs text-amber-600 dark:text-amber-400 pl-10 -mt-1">Seat limit reached — new invites will be refused.</p> : null}
            <Row icon={<HardDrive className="w-4 h-4" />} label="Storage limit" value={tenant.maxStorageGb ? `${tenant.maxStorageGb} GB` : '—'} />
            <Row icon={<Activity className="w-4 h-4" />} label="API calls / mo" value={(tenant.maxApiCallsPerMonth as number | undefined)?.toLocaleString() ?? '—'} />
          </CardContent>
        </Card>
      </div>

      {tenantId && (
        <>
          <TenantEntitlements tenantId={tenantId} canManage={canManageEntitlements} />
          <TenantCommercial tenantId={tenantId} canManage={canManageEntitlements} />
        </>
      )}

      {tenantId && canViewAuditAccess && (
        <TenantAuditAccess tenantId={tenantId} canManage={canManageAuditAccess} />
      )}
    </div>
  );
};

export default TenantDetailsPage;
